import React from 'react';
import { Lock } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import MoneyInput from '../shared/MoneyInput';
import NumberInput from '../shared/NumberInput';
import PickSelect from '../shared/PickSelect';
import AddressFields from '../shared/AddressFields';
import { emptyAddress, addressPayload, addressLine } from '../shared/address';
import { OPP_LOAN_TYPE_LABELS, OPP_LOAN_PURPOSE_LABELS, optionsFrom } from '../shared/labels';
import { fromApi } from '../kit/time';
import ManagedNote from './ManagedNote';

// Field set shared by the create dialogs and the drawer's edit form, for both
// boards (ticket 187). A descriptor is { key, label, kind, ... }; `kind` picks the
// input, the form-state shape and how the value is written back to the API:
//   money / int / number  number | null        (MoneyInput / NumberInput)
//   select / lender       string, '' = none    (PickSelect)
//   date                  'YYYY-MM-DD'         (sent as UTC midnight, like the other date inputs)
//   address               Address object       (legacy string rows load into `street`)
//   areas                 'a, b, c'            (sent as a list)
//   text / textarea       string, '' = none
// `side` limits a field to one RE side; `wide` spans both grid columns.

const opts = (map) => optionsFrom(map).map(([value, label]) => ({ value, label }));

export const MORTGAGE_FIELDS = [
  { key: 'loan_purpose', label: 'Loan Purpose', kind: 'select', options: opts(OPP_LOAN_PURPOSE_LABELS) },
  { key: 'loan_type', label: 'Loan Type', kind: 'select', options: opts(OPP_LOAN_TYPE_LABELS) },
  { key: 'purchase_price', label: 'Purchase Price', kind: 'money' },
  { key: 'loan_amount', label: 'Loan Amount', kind: 'money' },
  { key: 'credit_score', label: 'Credit Score', kind: 'int', min: 300, max: 850 },
  { key: 'lender_id', label: 'Lender', kind: 'lender' },
  // Virtual keys: both live inside the `rate_lock` subdocument.
  { key: 'rate_lock_rate', label: 'Locked Rate', kind: 'number', suffix: '%' },
  { key: 'rate_lock_expires_at', label: 'Rate Lock Expires', kind: 'date' },
  { key: 'preapproval_expires_at', label: 'Preapproval Expires', kind: 'date' },
  { key: 'estimated_closing_date', label: 'Est. Closing Date', kind: 'date' },
  { key: 'property_address', label: 'Property Address', kind: 'address' },
  { key: 'notes', label: 'Notes', kind: 'textarea', wide: true },
];

const PREAPPROVAL_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'none', label: 'None' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'expired', label: 'Expired' },
];

export const RE_FIELDS = [
  { key: 'target_price_min', label: 'Budget Min', kind: 'money', side: 'buyer' },
  { key: 'target_price_max', label: 'Budget Max', kind: 'money', side: 'buyer' },
  { key: 'approved_amount', label: 'Approved Amount', kind: 'money', side: 'buyer' },
  { key: 'preapproval_status', label: 'Preapproval', kind: 'select', options: PREAPPROVAL_OPTIONS, side: 'buyer' },
  { key: 'target_areas', label: 'Target Areas (comma separated)', kind: 'areas', side: 'buyer', wide: true },
  { key: 'listing_price', label: 'Listing Price', kind: 'money', side: 'seller' },
  { key: 'mls_number', label: 'MLS Number', kind: 'text', side: 'seller' },
  { key: 'listing_expiration', label: 'Listing Expires', kind: 'date', side: 'seller' },
  { key: 'property_address', label: 'Property Address', kind: 'address' },
  { key: 'notes', label: 'Notes', kind: 'textarea', wide: true },
];
export const reFields = (side) => RE_FIELDS.filter(f => !f.side || f.side === side);

// Fields the GHL / Arive guards own on a mirrored card (mirrors ghl_managed.OPPORTUNITY_FIELDS and
// arive_managed's list, restricted to what the form edits). The server stays the authority: a 409
// still locks a field the client did not predict.
export const GHL_LOCKED_FIELDS = ['loan_amount'];
export const ARIVE_LOCKED_FIELDS = ['loan_amount'];

// Terminal stages: the move routes set is_closed for these (mortgage_ and re_workspace_router),
// so a create form must not offer them.
export const MORTGAGE_CLOSED_STAGES = ['funded', 'denied', 'withdrawn'];
export const RE_CLOSED_STAGES = ['closed', 'lost', 'expired', 'withdrawn'];

const RATE_LOCK_KEYS = ['rate_lock_rate', 'rate_lock_expires_at'];

export const toDateInput = (iso) => {
  if (!iso) return '';
  const d = fromApi(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};
const toApiDate = (v) => (v ? new Date(`${v}T00:00:00Z`).toISOString() : null);

const read = (key, opp) => {
  if (key === 'rate_lock_rate') return opp?.rate_lock?.rate;
  if (key === 'rate_lock_expires_at') return opp?.rate_lock?.expires_at;
  return opp?.[key];
};

function startValue(f, opp) {
  const v = read(f.key, opp);
  switch (f.kind) {
    case 'address':
      return typeof v === 'string' ? { ...emptyAddress(), street: v } : { ...emptyAddress(), ...(v || {}) };
    case 'date': return toDateInput(v);
    case 'areas': return (opp?.target_areas || []).join(', ');
    case 'money': case 'int': case 'number': return v ?? null;
    default: return v ?? '';
  }
}

// Form state for `fields`, seeded from `opp` (null = blank create form).
export const buildForm = (fields, opp = null) => Object.fromEntries(fields.map(f => [f.key, startValue(f, opp)]));

function outValue(f, v) {
  switch (f.kind) {
    case 'address': return addressPayload(v);
    case 'date': return toApiDate(v);
    case 'areas': return String(v || '').split(',').map(s => s.trim()).filter(Boolean);
    case 'money': case 'int': case 'number': return v === '' || v === undefined ? null : v;
    default: { const s = String(v ?? '').trim(); return s === '' ? null : s; }
  }
}

const hasRateLock = (fields) => fields.some(f => f.key === RATE_LOCK_KEYS[0]);

// The whole `rate_lock` subdocument: the PATCH replaces it, so the keys the form does not edit
// (lender, locked_at, extended_until, status) are carried over from the card.
function rateLockOut(form, opp) {
  const rate = form.rate_lock_rate ?? null;
  const expires_at = toApiDate(form.rate_lock_expires_at);
  const prev = opp?.rate_lock || {};
  const set = rate != null || expires_at != null;
  const status = !set ? 'floating' : (prev.status && prev.status !== 'floating' ? prev.status : 'locked');
  return { ...prev, rate, expires_at, status };
}

// Create body: every field of the set, blanks as null/[] so the server defaults apply.
export function createPayload(fields, form) {
  const out = {};
  for (const f of fields) {
    if (RATE_LOCK_KEYS.includes(f.key)) continue;
    out[f.key] = outValue(f, form[f.key]);
  }
  if (hasRateLock(fields) && (form.rate_lock_rate != null || form.rate_lock_expires_at)) {
    out.rate_lock = rateLockOut(form, null);
  }
  return out;
}

// PATCH body: only what differs from `initial` (the form as first built), so a mirrored card is
// never sent a guarded field it did not change and `rate_lock` is not rewritten for nothing.
export function diffPayload(fields, form, initial, opp) {
  const out = {};
  const changed = (k) => JSON.stringify(form[k]) !== JSON.stringify(initial[k]);
  for (const f of fields) {
    if (!RATE_LOCK_KEYS.includes(f.key) && changed(f.key)) out[f.key] = outValue(f, form[f.key]);
  }
  if (hasRateLock(fields) && RATE_LOCK_KEYS.some(changed)) out.rate_lock = rateLockOut(form, opp);
  return out;
}

// A lender removed from the directory leaves its id on the cards that used it.
export const lenderName = (lenders, id) => (lenders || []).find(l => l.lender_id === id)?.name || 'Removed lender';

const fmt$ = (n) => (n == null || n === '' ? '—' : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }));

function shown(f, v, lenders) {
  if (v == null || v === '') return '—';
  if (f.kind === 'money') return fmt$(v);
  if (f.kind === 'address') return addressLine(v) || '—';
  if (f.kind === 'lender') return lenderName(lenders, v);
  if (f.kind === 'select') return f.options.find(o => o.value === v)?.label || v;
  return String(v);
}

// One field. `locked` renders the current value read-only (the server owns it on a mirrored card),
// with `lockNote` ({ text, href, link }: "Managed in GoHighLevel" + the link out) right beside it.
export function OppField({ f, value, onChange, locked = false, lockNote = null, lenders = [], testPrefix = 'opp', hint = null }) {
  const id = `${testPrefix}-${f.key.replace(/_/g, '-')}`;
  const wrap = f.wide ? 'col-span-2' : '';
  if (locked) {
    return (
      <div className={wrap}>
        <Label>{f.label}</Label>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-slate-600">
          <span className="flex items-center gap-1.5" data-testid={`${id}-locked`}>
            <Lock size={12} className="text-slate-400" /> <b style={{ color: '#070B5F' }}>{shown(f, value, lenders)}</b>
          </span>
          <ManagedNote note={lockNote} testId={`${id}-managed`} />
        </div>
      </div>
    );
  }
  if (f.kind === 'address') {
    return <AddressFields value={value} onChange={onChange} testIdPrefix={id} label={f.label} />;
  }
  if (f.kind === 'select' || f.kind === 'lender') {
    const options = f.kind === 'lender'
      ? [
          { value: '', label: 'No lender' },
          ...lenders.map(l => ({ value: l.lender_id, label: l.name })),
          ...(value && !lenders.some(l => l.lender_id === value) ? [{ value, label: lenderName(lenders, value) }] : []),
        ]
      : f.options;
    return (
      <div className={wrap}>
        <PickSelect label={f.label} labelClassName="text-sm font-medium leading-none" value={value} onChange={onChange} options={options} testId={id} />
      </div>
    );
  }
  let input;
  if (f.kind === 'money') input = <MoneyInput value={value} onChange={onChange} className="mt-1" data-testid={id} />;
  else if (f.kind === 'int') input = <NumberInput integer min={f.min} max={f.max} value={value} onChange={onChange} className="mt-1" data-testid={id} />;
  else if (f.kind === 'number') input = <NumberInput suffix={f.suffix} value={value} onChange={onChange} className="mt-1" data-testid={id} />;
  else if (f.kind === 'date') input = <Input type="date" value={value} onChange={(e) => onChange(e.target.value)} className="mt-1" data-testid={id} />;
  else if (f.kind === 'textarea') input = <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="mt-1" data-testid={id} />;
  else input = <Input value={value} onChange={(e) => onChange(e.target.value)} className="mt-1" data-testid={id} />;
  return (
    <div className={wrap}>
      <Label>{f.label}</Label>
      {input}
      {hint}
    </div>
  );
}

// The field grid. `locked` is a Set of keys rendered read-only, each showing `lockNote`; `hints` maps a
// key to a node shown under it.
export function OppFields({ fields, form, setField, locked, lockNote = null, lenders, testPrefix, hints = {} }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {fields.map(f => (
        <OppField key={f.key} f={f} value={form[f.key]} onChange={(v) => setField(f.key, v)}
          locked={!!locked?.has(f.key)} lockNote={lockNote} lenders={lenders} testPrefix={testPrefix} hint={hints[f.key]} />
      ))}
    </div>
  );
}
